import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import Layout from './pages/layout/Layout';
import PricingPage from './pages/pricing/PricingPage';
import { ROUTES } from './common/constants/routes';
import { ClerkSignedOut } from './common/clerk-components';
import { CompanyCreate } from './pages/company-create/CompanyCreate';
import SettingsPage from './pages/settings/SettingsPage';
import { NoCompanyHandler } from './pages/company-create/components/routes/NoCompanyHandler';
import { NoCompanyRedirect } from './common/no-company-redirect/NoCompanyRedirect';
import { RolesGateRedirect } from './common/roles-gate';
import { GROUPS } from './common/constants/groups';
import { useUserInfo } from './common/hooks/useUserInfo';
import DocumentVault from './pages/document-vault/DocumentVault';
import SigningPage from './pages/signing';
import DocumentsPage from './pages/documents';
import PageNotFound from './pages/page-not-found';
import ProtectedRoute from './common/roles-gate/protected-route';
import BusinessProfile from './pages/business-profile/BusinessProfile';
import EmployeeProfiles from './pages/employee-profiles/EmployeeProfiles';
import EmployeeProfile from './pages/employee-profiles/EmployeeProfile';
import { useCountriesInfo } from './common/hooks/useCountries';
import SupplierProfiles from './pages/supplier-profiles/SupplierProfiles';
import SupplierProfile from './pages/supplier-profiles/SupplierProfile';
import CustomerProfiles from './pages/customer-profiles/CustomerProfiles';
import CustomerProfile from './pages/customer-profiles/CustomerProfile';
import { CreateCustomerProfile } from './pages/customer-profiles/CreateCustomerProfile';
import { CreateSupplierProfile } from './pages/supplier-profiles/CreateSupplierProfile';
import DocumentDetails from './pages/document-details/DocumentDetails';
//import DocumentDetails from './pages/document-details-refactored';
import LibraryItemDetails from './pages/library-item-details/LibraryItemDetails';
import { useAuth } from '@clerk/clerk-react';
import EmployeeProfilePageForAdmin from './pages/employee-profiles/EmployeeProfilePageForAdmin';
import HomePage from './pages/home/HomePage';
import Library from './pages/library/Library';
import LogIn from './pages/login/LogIn';
import SignUp from './pages/signup/SignUp';
import DocumentsKeyDates from './pages/documents-key-dates/DocumentsKeyDates';
import Processing from './pages/processing';
import RegularError from './pages/regular-error';

const App = () => {
  const location = useLocation();
  const { isSignedIn } = useAuth();
  useUserInfo();
  useCountriesInfo();
  useEffect(() => {
    const head = document.head || document.getElementsByTagName('head')[0];
    const linkToRemove = document.getElementById('specialCanonicalLink');
    if (document.getElementById('specialCanonicalLink')) {
      head.removeChild(linkToRemove!);
    }
    if (location.pathname.includes(ROUTES.LOGIN) || location.pathname.includes(ROUTES.SIGN_UP)) {
      const linkElement = document.createElement('link');
      linkElement.rel = 'canonical';
      linkElement.href = location.pathname.includes(ROUTES.LOGIN)
        ? 'https://businesskitz.com/login'
        : 'https://businesskitz.com/signup';
      linkElement.id = 'specialCanonicalLink';
      head.appendChild(linkElement);
    }
    if (location.pathname !== ROUTES.LOGIN && location.pathname !== ROUTES.SIGN_UP) {
      localStorage.setItem('location', location.pathname);
    }
  }, [location.pathname]);
  return (
    <Routes>
      <Route path='*' element={<PageNotFound />} />
      <Route path={ROUTES.PROCESSING} element={<Processing />} />
      <Route path={ROUTES.REGULAR_ERROR} element={<RegularError />} />
      <Route
        path={ROUTES.INDEX}
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route
          path={ROUTES.BUSINESS_PROFILE}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  {/* <ProtectWrapper condition={(has) => has({ role: 'org:admin' }) } fallback={<div>Denied</div>}>
                    <BusinessProfile />
                  </ProtectWrapper> */}
                  <BusinessProfile />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SIGNING_DOCUMENT}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <SigningPage />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENTS}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <DocumentsPage />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENT_VAULT}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <DocumentVault />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENTS_KEY_DATES}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <DocumentsKeyDates />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENT_VAULT_CATEGORY_DOCUMENT}
          element={
            <ProtectedRoute>
              <NoCompanyRedirect>
                <DocumentDetails />
              </NoCompanyRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PRICING}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <PricingPage />
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.HOME}
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EMPLOYEE_PROFILES}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <EmployeeProfiles />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EMPLOYEE_PROFILE}
          element={
            <ProtectedRoute>
              <NoCompanyRedirect>
                <EmployeeProfile />
              </NoCompanyRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EMPLOYEE_PROFILE_CREATE}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <EmployeeProfilePageForAdmin />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUPPLIER_PROFILES}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <SupplierProfiles />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUPPLIER_PROFILES}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <SupplierProfiles />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUPPLIER_PROFILE_CREATE}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <CreateSupplierProfile />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SUPPLIER_PROFILE}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <SupplierProfile />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CUSTOMER_PROFILES}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <CustomerProfiles />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CUSTOMER_PROFILE_CREATE}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <CreateCustomerProfile />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CUSTOMER_PROFILE}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <CustomerProfile />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENT_LIBRARY}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <Library />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEMPLATE_ITEM}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <NoCompanyRedirect>
                  <LibraryItemDetails />
                </NoCompanyRedirect>
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.SETTINGS}
          element={
            <ProtectedRoute>
              <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
                <SettingsPage />
              </RolesGateRedirect>
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path={ROUTES.LOGIN}
        element={
          isSignedIn ? (
            <Navigate to={ROUTES.INDEX} />
          ) : (
            <ClerkSignedOut>
              <LogIn />
            </ClerkSignedOut>
          )
        }
      />
      <Route path={ROUTES.SIGN_UP} element={<SignUp />} />
      <Route
        path={ROUTES.COMPANY_CREATE}
        element={
          <NoCompanyHandler>
            <RolesGateRedirect group={GROUPS.COMPANY_ADMIN}>
              <CompanyCreate />
            </RolesGateRedirect>
          </NoCompanyHandler>
        }
      />
    </Routes>
  );
};

export default App;
